// AI assisted development
/**
 * Layout Component (T043)
 * 
 * Provides consistent page layout with header and navigation.
 */

import React, { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { 
  AccountTree as AccountTreeIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  CallMerge as CallMergeIcon,
  Assignment as AssignmentIcon,
  Source as SourceIcon,
  Groups as GroupsIcon,
  Settings as SettingsIcon,
  Lightbulb as LightbulbIcon,
  BugReport as BugReportIcon,
  Category as CategoryIcon,
  RocketLaunch as RocketLaunchIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ManageSearch as ManageSearchIcon,
  RateReview as RateReviewIcon,
  PersonSearch as PersonSearchIcon,
  Radar as RadarIcon,
  Psychology as PsychologyIcon,
  Error as ErrorIcon,
  Insights as InsightsIcon,
  Newspaper as NewspaperIcon,
  Menu as MenuIcon,
  KeyboardDoubleArrowLeft as HideNavigationIcon,
  KeyboardDoubleArrowRight as ShowNavigationIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { signIn, signOut } from '../services/auth';
import { AUTH_DISABLED } from '../config/auth';
import { DEVAGENT_ENABLED_BY_DEFAULT, useFeatureFlags, FeatureFlagNames } from '../hooks/useFeatureFlags';
import devagentIconUrl from '../assets/devagent-icon.svg';
import PageFeedbackButton from './feedback/PageFeedbackButton';
import NotificationCenter from './notifications/NotificationCenter';

interface LayoutProps {
  children: ReactNode;
}

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  iconColor?: string;
  path: string;
}

const DRAWER_WIDTH = 240;
const COMPACT_DRAWER_WIDTH = 64;
const MOBILE_DRAWER_WIDTH = 300;
const SIDEBAR_STATE_STORAGE_KEY = 'monocle.desktopSidebarState';
const SIDEBAR_VISIBLE_STATE_STORAGE_KEY = 'monocle.desktopSidebarVisibleState';
const SIDEBAR_STATE_VERSION_STORAGE_KEY = 'monocle.desktopSidebarStateVersion';
const SIDEBAR_STATE_VERSION = '2';

const NAVIGATION_ICON_COLORS = {
  organization: '#0078D4',
  engineers: '#008272',
  reportCard: '#107C10',
  pullRequests: '#F7630C',
  issues: '#D13438',
  repositories: '#CA5010',
  teams: '#8764B8',
  errors: '#C50F1F',
  pipelines: '#5C2D91',
  createPipeline: '#0078D4',
  analytics: '#008272',
  reviewIssue: '#0078D4',
  issueReviews: '#8764B8',
  issueReporters: '#008272',
  defectInvestigation: '#D13438',
  functionalAreas: '#498205',
  techRadar: '#005A9E',
  memory: '#744DA9',
  ideas: '#986F0B',
  releaseNotes: '#0078D4',
  admin: '#605E5C',
} as const;

type DesktopSidebarState = 'expanded' | 'compact' | 'hidden';
type VisibleDesktopSidebarState = Exclude<DesktopSidebarState, 'hidden'>;

function readStoredSidebarState(): DesktopSidebarState {
  if (typeof window === 'undefined') return 'compact';
  if (window.localStorage.getItem(SIDEBAR_STATE_VERSION_STORAGE_KEY) !== SIDEBAR_STATE_VERSION) {
    return 'compact';
  }

  const storedState = window.localStorage.getItem(SIDEBAR_STATE_STORAGE_KEY);
  return storedState === 'expanded' || storedState === 'hidden' ? storedState : 'compact';
}

function readStoredVisibleSidebarState(): VisibleDesktopSidebarState {
  if (typeof window === 'undefined') return 'compact';
  if (window.localStorage.getItem(SIDEBAR_STATE_VERSION_STORAGE_KEY) !== SIDEBAR_STATE_VERSION) {
    return 'compact';
  }

  return window.localStorage.getItem(SIDEBAR_VISIBLE_STATE_STORAGE_KEY) === 'expanded'
    ? 'expanded'
    : 'compact';
}

export default function Layout({ children }: LayoutProps) {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const [desktopSidebarState, setDesktopSidebarState] = useState<DesktopSidebarState>(readStoredSidebarState);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const lastVisibleSidebarState = useRef<VisibleDesktopSidebarState>(readStoredVisibleSidebarState());

  const desktopSidebarCompact = desktopSidebarState === 'compact';
  const desktopSidebarVisible = desktopSidebarState !== 'hidden';

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_STATE_VERSION_STORAGE_KEY, SIDEBAR_STATE_VERSION);
    window.localStorage.setItem(SIDEBAR_STATE_STORAGE_KEY, desktopSidebarState);
    if (desktopSidebarState !== 'hidden') {
      lastVisibleSidebarState.current = desktopSidebarState;
      window.localStorage.setItem(SIDEBAR_VISIBLE_STATE_STORAGE_KEY, desktopSidebarState);
    }
  }, [desktopSidebarState]);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);
  
  // Feature defaults fail closed until the API returns an explicit value.
  const isDefectInvestigationEnabled = isFeatureEnabled(FeatureFlagNames.ENABLE_DEFECT_INVESTIGATION, false);
  const isFunctionalAreaCatalogEnabled = isFeatureEnabled(
    FeatureFlagNames.ENABLE_FUNCTIONAL_AREA_CATALOG,
    false,
  );
  const isDevAgentEnabled = isFeatureEnabled(FeatureFlagNames.ENABLE_DEVAGENT, DEVAGENT_ENABLED_BY_DEFAULT);
  const isIssueQualityV2Enabled = isFeatureEnabled(FeatureFlagNames.ENABLE_ISSUE_QUALITY_V2, false);
  const isTechRadarEnabled = isFeatureEnabled(FeatureFlagNames.ENABLE_TECH_RADAR, false);
  const isMemoryEnabled = isFeatureEnabled(FeatureFlagNames.ENABLE_MEMORY, false);

  // Full-width pages: PR History, Issues, Repos, Teams, Engineers, Defect Investigation, DevAgent, Team Detail, Admin, Issue Reviews, Issue Reporters, Tech Radar, Memory, Errors
  const isFullWidth = ['/pr-history', '/issues', '/repos', '/teams', '/engineers', '/defect-investigation', '/functional-areas', '/devagent', '/admin', '/issue-reviews', '/issue-reporters', '/tech-radar', '/memory', '/errors'].includes(location.pathname) || location.pathname.startsWith('/teams/') || location.pathname.startsWith('/devagent/') || location.pathname.startsWith('/repos/') || location.pathname.startsWith('/issue-reporters/') || location.pathname.startsWith('/issue-reviews/');
  
  const handleSignIn = async () => {
    try {
      await signIn();
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };
  
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleNavigation = (path: string, closeMobileDrawer = false) => {
    navigate(path);
    if (closeMobileDrawer) setMobileDrawerOpen(false);
  };

  const toggleDesktopSidebarDensity = () => {
    setDesktopSidebarState((currentState) => currentState === 'compact' ? 'expanded' : 'compact');
  };

  const toggleDesktopSidebarVisibility = () => {
    setDesktopSidebarState((currentState) => (
      currentState === 'hidden' ? lastVisibleSidebarState.current : 'hidden'
    ));
  };
  
  // Main navigation menu items
  const mainMenuItems: NavigationItem[] = [
    { text: 'Organization', icon: <AccountTreeIcon />, iconColor: NAVIGATION_ICON_COLORS.organization, path: '/organization' },
    { text: 'Engineer Directory', icon: <PeopleIcon />, iconColor: NAVIGATION_ICON_COLORS.engineers, path: '/engineers' },
    { text: 'My Report Card', icon: <AssessmentIcon />, iconColor: NAVIGATION_ICON_COLORS.reportCard, path: '/report-card' },
    { text: 'Pull Requests', icon: <CallMergeIcon />, iconColor: NAVIGATION_ICON_COLORS.pullRequests, path: '/pr-history' },
    { text: 'Issues', icon: <AssignmentIcon />, iconColor: NAVIGATION_ICON_COLORS.issues, path: '/issues' },
    { text: 'Repos', icon: <SourceIcon />, iconColor: NAVIGATION_ICON_COLORS.repositories, path: '/repos' },
    { text: 'Teams', icon: <GroupsIcon />, iconColor: NAVIGATION_ICON_COLORS.teams, path: '/teams' },
    { text: 'Error Details', icon: <ErrorIcon />, iconColor: NAVIGATION_ICON_COLORS.errors, path: '/errors' },
  ];
  
  // DevAgent menu items - AI-powered pipeline management
  const devAgentMenuItems: NavigationItem[] = [
    {
      text: 'Dashboard',
      icon: <Box component="img" src={devagentIconUrl} alt="" sx={{ width: 24, height: 24 }} />,
      path: '/devagent/dashboard',
    },
    { text: 'Pipelines', icon: <RocketLaunchIcon />, iconColor: NAVIGATION_ICON_COLORS.pipelines, path: '/devagent/pipelines' },
    { text: 'Create Pipeline', icon: <AddCircleOutlineIcon />, iconColor: NAVIGATION_ICON_COLORS.createPipeline, path: '/devagent/create' },
    { text: 'Analytics', icon: <InsightsIcon />, iconColor: NAVIGATION_ICON_COLORS.analytics, path: '/devagent/analytics' },
  ];
  
  // Tools section menu items (conditionally built)
  const toolsMenuItems = useMemo(() => {
    const items: NavigationItem[] = [];
    
    // JIRA Quality v2 features (Feature 022) - feature flagged
    if (isIssueQualityV2Enabled) {
      items.push({ text: 'Review Issue', icon: <ManageSearchIcon />, iconColor: NAVIGATION_ICON_COLORS.reviewIssue, path: '/issue-review' });
      items.push({ text: 'Issue Reviews', icon: <RateReviewIcon />, iconColor: NAVIGATION_ICON_COLORS.issueReviews, path: '/issue-reviews' });
      items.push({ text: 'Issue Reporters', icon: <PersonSearchIcon />, iconColor: NAVIGATION_ICON_COLORS.issueReporters, path: '/issue-reporters' });
    }
    
    // Only show Defect Investigation if feature flag is enabled
    if (isDefectInvestigationEnabled) {
      items.push({ text: 'Defect Investigation', icon: <BugReportIcon />, iconColor: NAVIGATION_ICON_COLORS.defectInvestigation, path: '/defect-investigation' });
    }
    // Catalog page stays off until business-first review packages are ready
    if (isFunctionalAreaCatalogEnabled) {
      items.push({ text: 'Functional Areas', icon: <CategoryIcon />, iconColor: NAVIGATION_ICON_COLORS.functionalAreas, path: '/functional-areas' });
    }

    // Only show Tech Radar if feature flag is enabled
    if (isTechRadarEnabled) {
      items.push({ text: 'Tech Radar', icon: <RadarIcon />, iconColor: NAVIGATION_ICON_COLORS.techRadar, path: '/tech-radar' });
    }
    
    // Only show Memory if feature flag is enabled
    if (isMemoryEnabled) {
      items.push({ text: 'Memory', icon: <PsychologyIcon />, iconColor: NAVIGATION_ICON_COLORS.memory, path: '/memory' });
    }
    
    return items;
  }, [isIssueQualityV2Enabled, isDefectInvestigationEnabled, isFunctionalAreaCatalogEnabled, isTechRadarEnabled, isMemoryEnabled]);

  const footerMenuItems: NavigationItem[] = [
    { text: 'Ideas and Feedback', icon: <LightbulbIcon />, iconColor: NAVIGATION_ICON_COLORS.ideas, path: '/feedback' },
    { text: 'Release Notes', icon: <NewspaperIcon />, iconColor: NAVIGATION_ICON_COLORS.releaseNotes, path: '/release-notes' },
    { text: 'Admin', icon: <SettingsIcon />, iconColor: NAVIGATION_ICON_COLORS.admin, path: '/admin' },
  ];

  const isNavigationItemSelected = (path: string) => (
    location.pathname === path
    || (path === '/devagent/pipelines' && location.pathname.startsWith('/devagent/pipelines/'))
  );

  const renderNavigationItems = (
    items: NavigationItem[],
    compact: boolean,
    closeMobileDrawer: boolean,
  ) => items.map((item) => {
    const selected = isNavigationItemSelected(item.path);
    const navigationButton = (
      <ListItemButton
        selected={selected}
        onClick={() => handleNavigation(item.path, closeMobileDrawer)}
        aria-label={compact ? item.text : undefined}
        sx={{
          minHeight: 44,
          justifyContent: compact ? 'center' : 'initial',
          px: compact ? 1.25 : 2,
          borderLeft: '3px solid',
          borderLeftColor: selected ? 'primary.main' : 'transparent',
        }}
      >
        <ListItemIcon
          data-testid={`navigation-icon-${item.text.toLowerCase().replaceAll(' ', '-')}`}
          sx={{
            minWidth: compact ? 0 : 56,
            justifyContent: 'center',
            color: item.iconColor ?? 'text.secondary',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!compact && <ListItemText primary={item.text} />}
      </ListItemButton>
    );

    return (
      <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
        {compact ? (
          <Tooltip title={item.text} placement="right" arrow>
            {navigationButton}
          </Tooltip>
        ) : navigationButton}
      </ListItem>
    );
  });

  const renderNavigation = (compact: boolean, closeMobileDrawer: boolean) => (
    <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
        <List disablePadding={compact} sx={{ py: compact ? 1 : undefined }}>
          {renderNavigationItems(mainMenuItems, compact, closeMobileDrawer)}
        </List>

        {isDevAgentEnabled && (
          <>
            <Divider />
            <List
              disablePadding={compact}
              sx={{ py: compact ? 1 : undefined }}
              subheader={!compact ? (
                <ListSubheader component="div" sx={{ lineHeight: '36px', fontWeight: 600 }}>
                  DevAgent
                </ListSubheader>
              ) : undefined}
            >
              {renderNavigationItems(devAgentMenuItems, compact, closeMobileDrawer)}
            </List>
          </>
        )}

        {toolsMenuItems.length > 0 && (
          <>
            <Divider />
            <List
              disablePadding={compact}
              sx={{ py: compact ? 1 : undefined }}
              subheader={!compact ? (
                <ListSubheader component="div" sx={{ lineHeight: '36px', fontWeight: 600 }}>
                  Tools
                </ListSubheader>
              ) : undefined}
            >
              {renderNavigationItems(toolsMenuItems, compact, closeMobileDrawer)}
            </List>
          </>
        )}
      </Box>

      <Divider />
      <List disablePadding={compact} sx={{ py: compact ? 1 : undefined }}>
        {renderNavigationItems(footerMenuItems, compact, closeMobileDrawer)}
      </List>

      {!closeMobileDrawer && (
        <>
          <Divider />
          <Tooltip
            title={compact ? 'Show more information' : 'Show less information'}
            placement="right"
            arrow
          >
            <ListItemButton
              onClick={toggleDesktopSidebarDensity}
              aria-label={compact ? 'Show more information' : 'Show less information'}
              sx={{
                minHeight: 48,
                justifyContent: compact ? 'center' : 'flex-end',
                px: 1.25,
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                {compact ? <ShowNavigationIcon /> : <HideNavigationIcon />}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex', minWidth: 0, minHeight: '100vh' }}>
      {desktopSidebarVisible && (
        <Drawer
          variant="permanent"
          data-testid="desktop-navigation-drawer"
          sx={{
            width: desktopSidebarCompact ? COMPACT_DRAWER_WIDTH : DRAWER_WIDTH,
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shorter,
            }),
            '& .MuiDrawer-paper': {
              width: desktopSidebarCompact ? COMPACT_DRAWER_WIDTH : DRAWER_WIDTH,
              boxSizing: 'border-box',
              overflowX: 'hidden',
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
        >
          <Toolbar
            sx={{
              justifyContent: desktopSidebarCompact ? 'center' : 'flex-start',
              px: desktopSidebarCompact ? 0.75 : 2,
            }}
          >
            {desktopSidebarCompact ? (
              <Box
                component="span"
                aria-label="Monocle"
                data-testid="compact-monocle-mark"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'rgba(25, 118, 210, 0.34)',
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  lineHeight: 1,
                  overflow: 'visible',
                }}
              >
                🧐
              </Box>
            ) : (
              <Typography
                variant="h6"
                noWrap
                component="div"
                aria-label="Monocle"
                sx={{ fontSize: '0.95rem' }}
              >
                🧐 Monocle
              </Typography>
            )}
          </Toolbar>
          {renderNavigation(desktopSidebarCompact, false)}
        </Drawer>
      )}

      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: `min(${MOBILE_DRAWER_WIDTH}px, 88vw)`,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" noWrap component="div" sx={{ fontSize: '0.95rem' }}>
            🧐 Monocle
          </Typography>
          <IconButton onClick={() => setMobileDrawerOpen(false)} aria-label="Close navigation">
            <CloseIcon />
          </IconButton>
        </Toolbar>
        {renderNavigation(false, true)}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          minWidth: 0,
        }}
      >
        <AppBar position="static" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Toolbar sx={{ gap: { xs: 0.5, sm: 1 } }}>
            <Tooltip title="Open navigation">
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileDrawerOpen(true)}
                aria-label="Open navigation"
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={desktopSidebarVisible ? 'Hide navigation' : 'Show navigation'}>
              <IconButton
                color="inherit"
                edge="start"
                onClick={toggleDesktopSidebarVisibility}
                aria-label={desktopSidebarVisible ? 'Hide navigation' : 'Show navigation'}
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                {desktopSidebarVisible ? <HideNavigationIcon /> : <ShowNavigationIcon />}
              </IconButton>
            </Tooltip>

            <Typography
              variant="h6"
              component="div"
              noWrap
              sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                Monocle
              </Box>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Monocle Quality Dashboard
              </Box>
            </Typography>
            
            {AUTH_DISABLED ? (
              <>
                <NotificationCenter enablePipelineNotifications={isDevAgentEnabled} />
                <Typography variant="body2" sx={{ opacity: 0.9, display: { xs: 'none', sm: 'block' } }}>
                  Dev mode: authentication disabled
                </Typography>
              </>
            ) : isAuthenticated ? (
              <>
                <NotificationCenter enablePipelineNotifications={isDevAgentEnabled} />
                <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', lg: 'block' } }}>
                  {accounts[0]?.name || accounts[0]?.username}
                </Typography>
                <Button color="inherit" onClick={handleSignOut} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                  Sign Out
                </Button>
              </>
            ) : (
              <Button color="inherit" onClick={handleSignIn}>
                Sign In
              </Button>
            )}
          </Toolbar>
        </AppBar>
        
        <Container
          component="div"
          maxWidth={isFullWidth ? false : 'lg'}
          disableGutters={isFullWidth}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            py: { xs: 2, sm: 3, md: 4 },
            px: isFullWidth ? 0 : { xs: 2, sm: 3 },
          }}
        >
          {children}
        </Container>
        
        <Box component="footer" sx={{ py: 2, bgcolor: 'background.paper', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Monocle Code Review System
          </Typography>
        </Box>
        <PageFeedbackButton />
      </Box>
    </Box>
  );
}
